import {
  ChatPromptToolLink,
  ChatTool,
  ChatToolCreateRequest,
  ChatToolLinkedPrompt,
  ChatToolListResponse,
  ChatToolUpdateRequest
} from '../types/chatTool';
import { PaginationParams } from '../types/pagination';
import { ApiClient } from './apiClient';

export class ChatToolService {
  constructor(private api: ApiClient) { }

  async list(params: PaginationParams & { active?: boolean; search?: string }) {
    const query: Record<string, string | number | undefined> = {
      page: params.page ?? 1,
      limit: params.limit ?? 20
    };

    if (typeof params.active === 'boolean') {
      query.active = params.active ? 'true' : 'false';
    }

    if (params.search !== undefined && params.search !== '') {
      query.search = params.search;
    }

    const result = await this.api.get<ChatToolListResponse>('/api/chat-tools', query);
    const response = result.success && result.data
      ? result.data
      : {
        data: [],
        meta: {
          page: Number(params.page ?? 1),
          limit: Number(params.limit ?? 20),
          total: 0,
          totalPages: 0
        }
      };

    return {
      data: response.data ?? [],
      meta: response.meta ?? {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
      }
    };
  }

  getById(id: string) {
    return this.api
      .get<ChatTool>(`/api/chat-tools/${id}`)
      .then(result => (result.success && result.data ? result.data : null));
  }

  create(payload: ChatToolCreateRequest) {
    return this.api
      .post<ChatTool>('/api/chat-tools', payload)
      .then(result => (result.success && result.data ? result.data : null));
  }

  update(id: string, payload: ChatToolUpdateRequest) {
    return this.api
      .put<ChatTool>(`/api/chat-tools/${id}`, payload)
      .then(result => (result.success && result.data ? result.data : null));
  }

  delete(id: string) {
    return this.api.delete<void>(`/api/chat-tools/${id}`).then(() => undefined);
  }

  getByPrompt(promptId: number) {
    return this.api
      .get<ChatTool[]>(`/api/chat-tools/by-prompt/${promptId}`)
      .then(result => (result.success && result.data ? result.data : []));
  }

  replacePromptLinks(promptId: number, links: ChatPromptToolLink[]) {
    return this.api
      .put<void>(`/api/chat-tools/by-prompt/${promptId}`, links)
      .then(result => (result.success ? undefined : undefined));
  }

  /**
   * Linked prompts for a tool (read-only). Prefers `prompts` on tool detail;
   * falls back to `GET /api/chat-prompts/by-tool/:toolId` when present.
   */
  async getLinkedPrompts(toolId: string): Promise<ChatToolLinkedPrompt[]> {
    const detail = await this.getById(toolId);
    if (detail?.prompts !== undefined) {
      return Array.isArray(detail.prompts) ? detail.prompts : [];
    }

    const result = await this.api.get<ChatToolLinkedPrompt[]>(
      `/api/chat-prompts/by-tool/${toolId}`,
      undefined,
      { skipErrorNotification: true }
    );
    if (result.success && Array.isArray(result.data)) {
      return result.data;
    }
    return [];
  }
}

